import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { formatPrice } from '@spotahome/soyuz-currency';
import { getResourcePath } from '@spotahome/soyuz/client';

import MapsAPI from '../../ui/map/maps-api';

const HOVER_MARKER_PATH = getResourcePath(
  '/bundles/sahapp/images/ui/marker-price-selected.svg'
);
const MARKER_PATH = getResourcePath(
  '/bundles/sahapp/images/ui/marker-price.svg'
);
const POI_MARKER_PATH = getResourcePath(
  '/bundles/sahapp/images/ui/marker-point-of-interest.svg'
);

const SELECT_MARKER_PATH = getResourcePath(
  '/bundles/sahapp/images/ui/select-marker-price.svg'
);

const SELECT_HOVER_MARKER_PATH = getResourcePath(
  '/bundles/sahapp/images/ui/select-marker-price-selected.svg'
);

const LABEL_COLOR = '#5c5c5c';
const LABEL_HOVER_COLOR = '#ffffff';
const SELECT_LABEL_COLOR = '#8143ff';

const MARKER_ZINDEX = 1000;
const POI_MARKER_ZINDEX = 1010;
const HOVER_MARKER_ZINDEX = 3000;
const MAP_PADDING = 40;

const ADD_BIG_MARKERS_TIME = 400;

const MIN_ZOOM = 1;
const MAX_ZOOM = 20;

class MarkersWithPriceMap extends Component {
  static propTypes = {
    mapId: PropTypes.string.isRequired,
    city: PropTypes.shape({
      center: PropTypes.object.isRequired
    }).isRequired,
    defaultZoom: PropTypes.number.isRequired,
    markerList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    pointOfInterest: PropTypes.object,
    adIdCardMouseEnter: PropTypes.string,
    adIdCardMouseLeave: PropTypes.string,
    adMarkerClicked: PropTypes.object,
    adMarkerMouseEnter: PropTypes.object,
    adMarkerMouseLeave: PropTypes.object,
    onMarkerClicked: PropTypes.func.isRequired,
    onMarkerMouseEnter: PropTypes.func.isRequired,
    onMarkerMouseLeave: PropTypes.func.isRequired,
    onMapDidMount: PropTypes.func.isRequired,
    onMapWillUnmount: PropTypes.func.isRequired,
    onIdle: PropTypes.func.isRequired,
    currencyIsoCode: PropTypes.string.isRequired
  };

  static defaultProps = {
    pointOfInterest: null,
    adIdCardMouseEnter: '',
    adIdCardMouseLeave: '',
    adMarkerClicked: '',
    adMarkerMouseEnter: null,
    adMarkerMouseLeave: null
  };

  _addPointOfInterest(pointOfInterest) {
    this.map.addMarker('pointOfInterest', {
      lat: parseFloat(pointOfInterest.lat),
      lng: parseFloat(pointOfInterest.lng),
      icon: POI_MARKER_PATH,
      withAnimation: true,
      zIndex: POI_MARKER_ZINDEX
    });

    this.map.addCircle('pointOfInterest', {
      lat: parseFloat(pointOfInterest.lat),
      lng: parseFloat(pointOfInterest.lng),
      color: '#32B496',
      radius: 1000
    });
  }

  _removePointOfInterest() {
    this.map.removeMarker('pointOfInterest');
    this.map.removeCircle('pointOfInterest');
  }

  _onMarkerClicked(elemId) {
    this.props.onMarkerClicked(elemId);
  }

  _onMarkerMouseEnter(elemId) {
    this.props.onMarkerMouseEnter(elemId);
  }

  _onMarkerMouseLeave(elemId) {
    this.props.onMarkerMouseLeave(elemId);
  }

  _addMarkers(markerList, pointOfInterest) {
    if (this.props.markerList && this.props.markerList.length > 0) {
      const markersToBeRemoved = _.difference(
        this.props.markerList,
        markerList
      );
      this._removeMarkers(markersToBeRemoved);
    }

    if (markerList && markerList.length > 0) {
      if (this.initializePosition) {
        const markerCoords = [];
        markerList.forEach(elem => {
          markerCoords.push({
            lat: parseFloat(elem.coord[1]),
            lng: parseFloat(elem.coord[0])
          });
        });

        if (pointOfInterest) {
          markerCoords.push(pointOfInterest);
        }

        this.map.fitBoundsToCoords(markerCoords);
        this.initializePosition = false;
      }

      const sortedMarkers = this._sortMarkersByLat(markerList);
      this.markersCount = this._generateMarkerZIndex(
        sortedMarkers,
        MARKER_ZINDEX
      );
      this._addMarkersWithAnimation(sortedMarkers, ADD_BIG_MARKERS_TIME);
    }
  }

  _sortMarkersByLat = markerList => {
    const sortedMarkers = markerList.slice(0);

    sortedMarkers.sort((a, b) => b.coord[1] - a.coord[1]);

    return sortedMarkers;
  };

  _generateMarkerZIndex = (markerList, initialZIndex) => {
    let zIndex = initialZIndex;
    const markersCount = {};
    markerList.forEach(elem => {
      markersCount[elem.id] = {
        id: elem.id,
        coord: elem.coord,
        rentableUnitIds: elem.rentableUnits.map(e => e.id),
        zIndex
      };

      zIndex += 10;
    });
    return markersCount;
  };

  _addMarkersWithAnimation(markers) {
    markers.forEach(elem => {
      if (!this.map.isMarkerDefined(elem.id)) {
        this.map.addMarker(elem.id, {
          lat: parseFloat(elem.coord[1]),
          lng: parseFloat(elem.coord[0]),
          icon: this._getBigMarkerIcon(elem.id, markers),
          zIndex: this._getBigMarkerZIndex(elem.id),
          label: {
            color: this.renderMarkerLabel(elem.id, markers),
            text: `${formatPrice(
              elem.minimumPrice,
              this.props.currencyIsoCode
            )}`,
            fontFamily: 'Helvetica',
            fontSize: '14px',
            fontWeight: 'bold'
          }
        });

        this._setBigMarkerListener(elem);
      }
    });
  }

  _removeMarkers(markers) {
    if (markers) {
      markers.forEach(elem => {
        this.map.removeMarker(elem.id);
      });
    }
  }

  _setBigMarkerListener(elem) {
    this.map.replaceMarkerListener(elem.id, 'click', () => {
      this._onMarkerClicked(this.markersCount[elem.id]);
    });

    this.map.replaceMarkerListener(elem.id, 'mouseover', () => {
      this._onMarkerMouseEnter(this.markersCount[elem.id]);
    });

    this.map.replaceMarkerListener(elem.id, 'mouseout', () => {
      this._onMarkerMouseLeave(this.markersCount[elem.id]);
    });
  }

  _setBigMarkerIcon(markerId, markers) {
    if (markerId && this.markersCount[markerId]) {
      this.map.changeMarkerIcon(
        markerId,
        this._getBigMarkerIcon(markerId, markers)
      );
      this.map.changeMarkerZIndex(
        markerId,
        this._getBigMarkerZIndex(markerId, markers)
      );

      const label = this.map.getMarkerLabel(markerId);
      if (label) {
        label.color = this.renderMarkerLabel(markerId, markers);
        this.map.changeMarkerLabel(markerId, label);
      }
    }
  }

  _setBigHoverMarkerIcon(markerId, markers) {
    if (markerId && this.markersCount[markerId]) {
      this.map.changeMarkerIcon(
        markerId,
        this._getBigHoverMarkerIcon(markerId, markers)
      );
      this.map.changeMarkerZIndex(markerId, HOVER_MARKER_ZINDEX);
      const label = this.map.getMarkerLabel(markerId);
      if (label) {
        label.color = LABEL_HOVER_COLOR;
        this.map.changeMarkerLabel(markerId, label);
      }
    }
  }

  _getBigMarkerZIndex(markerId) {
    if (this.markersCount[markerId]) {
      return this.markersCount[markerId].zIndex;
    }

    return MARKER_ZINDEX;
  }

  isMarkerSelect = (markerId, markers) => {
    const selectedMarker = markers.find(
      marker => marker.id === parseInt(markerId)
    );
    return selectedMarker ? selectedMarker.isSelect : false;
  };

  renderMarkerLabel = (markerId, markers) => {
    const isSelect = this.isMarkerSelect(markerId, markers);

    return isSelect ? SELECT_LABEL_COLOR : LABEL_COLOR;
  };

  _getBigMarkerIcon = (markerId, markers) => {
    const isSelect = this.isMarkerSelect(markerId, markers);
    return isSelect ? SELECT_MARKER_PATH : MARKER_PATH;
  };

  _getBigHoverMarkerIcon = (markerId, markers) => {
    const isSelect = this.isMarkerSelect(markerId, markers);
    return isSelect ? SELECT_HOVER_MARKER_PATH : HOVER_MARKER_PATH;
  };

  isMarkerInBoundaries(coords) {
    return this.map.coordsInBounds(coords, MAP_PADDING);
  }

  componentDidMount() {
    this.initializePosition = true;
    this.markersCount = {};

    this.map = new MapsAPI(this.props.mapId, {
      zoom: this.props.defaultZoom,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      transitLayer: true,
      styles: [
        {
          stylers: [
            {
              saturation: -50
            },
            {
              lightness: 10
            }
          ]
        },
        {
          featureType: 'administrative',
          stylers: [
            {
              saturation: 55
            }
          ]
        },
        {
          featureType: 'administrative',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#6c6c6c'
            },
            {
              visibility: 'on'
            },
            {
              weight: 1.5
            }
          ]
        },
        {
          featureType: 'poi',
          elementType: 'labels.icon',
          stylers: [
            {
              saturation: 20
            },
            {
              lightness: 5
            },
            {
              visibility: 'off'
            }
          ]
        },
        {
          featureType: 'poi',
          elementType: 'labels.text.fill',
          stylers: [
            {
              saturation: -80
            }
          ]
        },
        {
          featureType: 'poi.business',
          stylers: [
            {
              visibility: 'simplified'
            }
          ]
        },
        {
          featureType: 'poi.park',
          stylers: [
            {
              saturation: 10
            },
            {
              lightness: 5
            }
          ]
        },
        {
          featureType: 'road',
          stylers: [
            {
              saturation: 35
            },
            {
              lightness: 45
            }
          ]
        },
        {
          featureType: 'transit.line',
          stylers: [
            {
              saturation: -50
            }
          ]
        },
        {
          featureType: 'transit.station',
          elementType: 'labels.icon',
          stylers: [
            {
              saturation: 40
            }
          ]
        },
        {
          featureType: 'transit.station',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#00adff'
            },
            {
              saturation: -30
            },
            {
              visibility: 'on'
            }
          ]
        },
        {
          featureType: 'water',
          stylers: [
            {
              saturation: 80
            },
            {
              lightness: -5
            }
          ]
        }
      ]
    });

    this.map.setCenter({
      lat: parseFloat(this.props.city.center.lat),
      lng: parseFloat(this.props.city.center.lng)
    });

    if (this.props.onMapDidMount) {
      this.props.onMapDidMount(this);
    }

    this._addMarkers(this.props.markerList, this.props.pointOfInterest);

    if (this.props.onIdle) {
      this.map.addMapListener('idle', () => {
        const bounds = this.map.getBounds();
        this.props.onIdle(bounds);
      });
    }

    if (this.props.pointOfInterest) {
      this._addPointOfInterest(this.props.pointOfInterest);
    } else {
      this._removePointOfInterest();
    }
  }

  setInitializePosition(value) {
    this.initializePosition = value;
  }

  componentWillUnmount() {
    this.map.removeMapListeners();
    this.props.onMapWillUnmount();
  }

  componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.markerList, nextProps.markerList)) {
      this._addMarkers(nextProps.markerList, nextProps.pointOfInterest);
    }

    if (!_.isEqual(this.props.adMarkerClicked, nextProps.adMarkerClicked)) {
      if (this.props.adMarkerClicked) {
        if (this.markersCount[this.props.adMarkerClicked.id]) {
          this._setBigMarkerIcon(
            this.props.adMarkerClicked.id,
            nextProps.markerList
          );
        }
      }

      if (nextProps.adMarkerClicked) {
        if (this.markersCount[nextProps.adMarkerClicked.id]) {
          this._setBigHoverMarkerIcon(
            nextProps.adMarkerClicked.id,
            nextProps.markerList
          );
        }
      }
    }

    if (this.props.pointOfInterest !== nextProps.pointOfInterest) {
      this.initializePosition = true;
      if (nextProps.pointOfInterest) {
        this._addPointOfInterest(nextProps.pointOfInterest);
      } else {
        this._removePointOfInterest();
      }
    }

    if (this.props.adIdCardMouseEnter !== nextProps.adIdCardMouseEnter) {
      this._setBigMarkerIcon(
        this.props.adIdCardMouseEnter,
        nextProps.markerList
      );
      this._setBigHoverMarkerIcon(
        nextProps.adIdCardMouseEnter,
        nextProps.markerList
      );
    }

    if (this.props.adIdCardMouseLeave !== nextProps.adIdCardMouseLeave) {
      this._setBigMarkerIcon(
        nextProps.adIdCardMouseLeave,
        nextProps.markerList
      );
    }

    if (this.props.adMarkerMouseEnter !== nextProps.adMarkerMouseEnter) {
      this._setBigMarkerIcon(
        this.props.adIdCardMouseEnter,
        nextProps.markerList
      );
      this._setBigHoverMarkerIcon(
        nextProps.adIdCardMouseEnter,
        nextProps.markerList
      );
      this._setBigMarkerIcon(
        nextProps.adIdCardMouseLeave,
        nextProps.markerList
      );

      if (nextProps.adMarkerMouseEnter) {
        const adIdEnter = nextProps.adMarkerMouseEnter.id;
        if (this.markersCount[adIdEnter]) {
          this.map.changeMarkerIcon(
            adIdEnter,
            this._getBigHoverMarkerIcon(adIdEnter, nextProps.markerList)
          );

          this.map.changeMarkerZIndex(adIdEnter, HOVER_MARKER_ZINDEX);

          const label = this.map.getMarkerLabel(adIdEnter);
          if (label) {
            label.color = LABEL_HOVER_COLOR;
            this.map.changeMarkerLabel(adIdEnter, label);
          }
        }
      }
    }

    if (this.props.adMarkerMouseLeave !== nextProps.adMarkerMouseLeave) {
      if (nextProps.adMarkerMouseLeave) {
        if (
          !_.isEqual(nextProps.adMarkerMouseLeave, this.props.adMarkerClicked)
        ) {
          const adIdLeave = nextProps.adMarkerMouseLeave.id;
          if (this.markersCount[adIdLeave]) {
            this.map.changeMarkerIcon(
              adIdLeave,
              this._getBigMarkerIcon(adIdLeave, nextProps.markerList)
            );

            this.map.changeMarkerZIndex(
              adIdLeave,
              this._getBigMarkerZIndex(adIdLeave)
            );

            const label = this.map.getMarkerLabel(adIdLeave);
            if (label) {
              label.color = this.renderMarkerLabel(
                adIdLeave,
                nextProps.markerList
              );
              this.map.changeMarkerLabel(adIdLeave, label);
            }
          }
        }
      }
    }
  }
  render() {
    return <div className="l-map" id={this.props.mapId} data-cy="themap" />;
  }
}

export default MarkersWithPriceMap;
